import axios, { type AxiosError } from 'axios';
import type {
  AuthResponse,
  MeResponse,
  Room,
  RoomListResponse,
  UploadResponse,
  Video,
  VideoListResponse,
  Visibility,
} from '../types';

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

export const AUTH_TOKEN_KEY = 'abr_auth_token';

let unauthorizedHandler: (() => void) | null = null;

function safeStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Safari can block storage in private/restricted modes; keep the app renderable.
  }
}

function safeStorageRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore storage removal failures for restricted browsers.
  }
}

export const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

export function getAuthToken(): string | null {
  return safeStorageGet(AUTH_TOKEN_KEY);
}

export function setAuthToken(token: string | null): void {
  if (token) {
    safeStorageSet(AUTH_TOKEN_KEY, token);
  } else {
    safeStorageRemove(AUTH_TOKEN_KEY);
  }
}

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      setAuthToken(null);
      unauthorizedHandler?.();
    }
    return Promise.reject(error);
  }
);

export async function registerUser(
  login: string,
  password: string,
  name?: string
): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/api/auth/register', {
    login,
    password,
    name,
  });
  return response.data;
}

export async function loginUser(login: string, password: string): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/api/auth/login', {
    login,
    password,
  });
  return response.data;
}

export async function fetchMe(): Promise<MeResponse> {
  const response = await apiClient.get<MeResponse>('/api/auth/me');
  return response.data;
}

export async function updateAccountLogin(login: string): Promise<AuthResponse> {
  const response = await apiClient.patch<AuthResponse>('/api/account/login', { login });
  return response.data;
}

export async function updateAccountPassword(
  currentPassword: string,
  newPassword: string
): Promise<{ success: true }> {
  const response = await apiClient.patch<{ success: true }>('/api/account/password', {
    currentPassword,
    newPassword,
  });
  return response.data;
}

export async function uploadAccountAvatar(file: File): Promise<MeResponse> {
  const formData = new FormData();
  formData.append('avatar', file);

  const response = await apiClient.post<MeResponse>('/api/account/avatar', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function uploadVideo(
  file: File,
  title: string,
  onUploadProgress?: (percent: number) => void,
  selectedProfiles?: string[],
  visibility: Visibility = 'public'
): Promise<UploadResponse> {
  const formData = new FormData();
  formData.append('video', file);
  formData.append('title', title);
  formData.append('visibility', visibility);
  if (selectedProfiles && selectedProfiles.length > 0) {
    formData.append('profiles', JSON.stringify(selectedProfiles));
  }

  const response = await apiClient.post<UploadResponse>('/api/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 0,
    onUploadProgress: (event) => {
      if (event.total && onUploadProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onUploadProgress(percent);
      }
    },
  });

  return response.data;
}

export async function fetchVideos(): Promise<VideoListResponse> {
  const response = await apiClient.get<VideoListResponse>('/api/videos');
  return response.data;
}

export async function fetchVideo(id: string): Promise<Video> {
  const response = await apiClient.get<Video>(`/api/videos/${id}`);
  return response.data;
}

export async function deleteVideo(id: string): Promise<void> {
  await apiClient.delete(`/api/videos/${id}`);
}

export async function createRoom(videoId: string, name: string, visibility: Visibility): Promise<Room> {
  const response = await apiClient.post<Room>('/api/rooms', { videoId, name, visibility });
  return response.data;
}

export async function fetchRooms(): Promise<RoomListResponse> {
  const response = await apiClient.get<RoomListResponse>('/api/rooms');
  return response.data;
}

export async function fetchRoom(id: string): Promise<Room> {
  const response = await apiClient.get<Room>(`/api/rooms/${id}`);
  return response.data;
}

export async function joinRoom(id: string): Promise<Room> {
  const response = await apiClient.post<Room>(`/api/rooms/${id}/join`);
  return response.data;
}

export async function leaveRoom(id: string): Promise<void> {
  await apiClient.post(`/api/rooms/${id}/leave`);
}

export async function deleteRoom(id: string): Promise<void> {
  await apiClient.delete(`/api/rooms/${id}`);
}

export function getStreamUrl(relativePath: string, includeToken = false): string {
  const token = includeToken ? getAuthToken() : null;
  const separator = relativePath.includes('?') ? '&' : '?';
  const pathWithToken = token
    ? `${relativePath}${separator}token=${encodeURIComponent(token)}`
    : relativePath;

  if (BASE_URL) {
    return `${BASE_URL}${pathWithToken}`;
  }
  return pathWithToken;
}

export function getAssetUrl(relativePath: string | null | undefined): string | null {
  if (!relativePath) return null;
  if (/^(https?:|data:)/i.test(relativePath)) return relativePath;
  const normalizedPath = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
  if (BASE_URL) {
    return `${BASE_URL}${normalizedPath}`;
  }
  return normalizedPath;
}

export function getSocketUrl(): string {
  return BASE_URL || window.location.origin;
}
